import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "bootstrap/dist/css/bootstrap.min.css";
import { useTranslation } from "react-i18next";

const CookiesConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Check if user has already consented
    const hasConsent = localStorage.getItem("libascookieConsent");

    if (!hasConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);

      // Prevent scrolling when banner is visible
      if (isVisible) {
        document.body.style.overflow = "hidden";
      }

      return () => {
        clearTimeout(timer);
        document.body.style.overflow = "auto";
      };
    }
  }, [isVisible]);

  const acceptAll = () => {
    setIsClosing(true);
    setTimeout(() => {
      localStorage.setItem("libascookieConsent", "accepted");
      setIsVisible(false);
      setIsClosing(false);
    }, 600);
  };

  const continueWithoutAccepting = () => {
    setIsClosing(true);
    setTimeout(() => {
      localStorage.setItem("libascookieConsent", "rejected");
      setIsVisible(false);
      setIsClosing(false);
    }, 600);
  };

  // Enhanced animations
  const bannerVariants = {
    hidden: { y: 100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 40,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
    exit: {
      y: 100,
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        when: "afterChildren",
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  };

  // Staggered children animations
  const childVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
    exit: {
      y: 10,
      opacity: 0,
      transition: { duration: 0.3 },
    },
  };

  // Button hover animation
  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: "0px 5px 10px rgba(198, 172, 150, 0.3)",
      transition: { duration: 0.2 },
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 },
    },
  };

  // More pronounced overlay fade animation
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.5, delay: 0.2 },
    },
  };

  // For demo purposes
  const resetConsent = () => {
    localStorage.removeItem("libascookieConsent");
    setIsVisible(true);
  };

  // Custom styles
  const styles = {
    resetButton: {
      position: "fixed",
      top: "20px",
      right: "20px",
      zIndex: 1060,
    },
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      zIndex: 1050,
      backdropFilter: "blur(4px)",
    },
    banner: {
      backgroundColor: "#FFFFFF",
      borderTop: "2px solid #C6AC96",
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1051,
    },
    heading: {
      color: "#231F20",
      fontFamily: "'Playfair Display', serif",
    },
    text: {
      color: "#231F20",
    },
    acceptButton: {
      backgroundColor: "#C6AC96",
      color: "#FFFFFF",
      border: "none",
      transition: "all 0.3s ease",
    },
    progressBar: {
      height: "2px",
      backgroundColor: "#C6AC96",
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
    },
    cookieIcon: {
      display: "inline-block",
    },
  };

  return (
    <>
      {/* Demo Reset Button - Remove in production */}
      {/* <motion.button
        onClick={resetConsent}
        className="btn btn-sm btn-light border mt-5"
        style={styles.resetButton}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Reset Consent (Demo)
      </motion.button> */}

      {/* Consent Banner with Overlay */}
      <AnimatePresence>
        {isVisible && (
          <>
            {/* Dark overlay to prevent interaction with the page */}
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={overlayVariants}
              style={styles.overlay}
            />

            {/* Cookie consent banner */}
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={bannerVariants}
              className="shadow-lg montserrat-font"
              style={styles.banner}
            >
              {/* Progress bar for closing animation */}
              {isClosing && (
                <motion.div
                  style={styles.progressBar}
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                />
              )}

              <div className="container py-4">
                <div className="row">
                  <div className="col-12">
                    <motion.div
                      className="mb-3 d-flex align-items-center"
                      variants={childVariants}
                    >
                      <motion.span
                        role="img"
                        aria-label="cookie"
                        className="fs-3 me-2"
                        style={styles.cookieIcon}
                        initial={{ rotate: -20 }}
                        animate={{
                          rotate: [0, -10, 0, 10, 0],
                          scale: [1, 1.2, 1, 1.2, 1],
                        }}
                        transition={{
                          duration: 0.8,
                          delay: 0.5,
                          ease: "easeInOut",
                        }}
                      >
                        🍪
                      </motion.span>
                      <motion.h3
                        className="mb-0 fw-medium fs-4 consent-cookies-heading"
                        id="consent-cookies-heading"
                        style={styles.heading}
                        variants={childVariants}
                      >
                        {i18n.language === "en"
                          ? "We respect your privacy and your data"
                          : "نحن نحترم خصوصيتك وبياناتك"}
                      </motion.h3>
                    </motion.div>

                    <motion.p
                      className="mb-3 fs-6 "
                      id="consent-cookies-paragraph"
                      style={styles.text}
                      variants={childVariants}
                    >
                      {i18n.language === "en"
                        ? "Libas uses cookies to enhance your shopping experience. Cookies help us remember your preferences, personalize your experience, and analyze site traffic to improve our services. By accepting, you allow us to deliver a seamless experience tailored to your needs."
                        : "تستخدم شركة ليباس الدولية ملفات تعريف الارتباط لتعزيز تجربة التسوق الخاصة بك. تساعدنا هذه الملفات في تذكر تفضيلاتك، وتخصيص تجربتك، وتحليل حركة المرور لتحسين خدماتنا. بقبولك، فإنك تسمح لنا بتقديم تجربة سلسة مصممة خصيصًا لك."}
                    </motion.p>

                    <motion.div
                      className="d-flex justify-content-between align-items-center flex-wrap gap-3"
                      variants={childVariants}
                    >
                      <div className="d-flex gap-4">
                        {/* <motion.a
                          href="/privacy-policy"
                          className="text-decoration-underline fs-6"
                          style={styles.text}
                          whileHover={{
                            x: 3,
                            textShadow: "0px 0px 3px rgba(0,0,0,0.1)",
                          }}
                          id="consent-cookies-links"
                        >
                          {i18n.language === "en"
                            ? "Privacy Policy"
                            : "سياسة الخصوصية"}
                        </motion.a> */}

                        <motion.button
                          onClick={continueWithoutAccepting}
                          className="text-decoration-underline bg-transparent border-0 p-0 fs-6"
                          style={styles.text}
                          whileHover={{
                            x: 3,
                            textShadow: "0px 0px 3px rgba(0,0,0,0.1)",
                          }}
                          id="consent-cookies-links"
                        >
                          {i18n.language === "en"
                            ? "Continue without accepting"
                            : "المتابعة دون قبول"}
                        </motion.button>
                      </div>

                      <motion.button
                        onClick={acceptAll}
                        className="btn px-4 py-2 rounded-pill"
                        style={styles.acceptButton}
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                        initial={{
                          boxShadow: "0px 0px 0px rgba(198, 172, 150, 0)",
                        }}
                      >
                        {i18n.language === "en"
                          ? "Accept All Cookies"
                          : "قبول جميع ملفات تعريف الارتباط"}
                      </motion.button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookiesConsent;
